/**
 * Auto tabs — "Experiences designed for discerning guests" (Events and Community, TZ section 5).
 * The active tab's progress line fills up (CSS animation, --at-duration); when it ends the next tab opens
 * (loops) and the photo crossfades. Click / ←→ switch manually and restart the line.
 * Paused while the section is off-screen or the browser tab is hidden (.is-paused → animation-play-state).
 */
export const autoTabs = () => {
    return new Promise((resolve) => {
        document.querySelectorAll('.auto-tabs').forEach((section) => init(section));
        resolve();
    });
};

const init = (section) => {
    const items = [...section.querySelectorAll('.auto-tabs__item')];
    const tabs = items.map((item) => item.querySelector('.auto-tabs__tab'));
    const images = [...section.querySelectorAll('.auto-tabs__image')];
    if (items.length < 2) return;

    let current = 0;

    const activate = (index, focus = false) => {
        current = (index + items.length) % items.length;
        items.forEach((item, i) => {
            const isActive = i === current;
            // re-adding the class restarts the line animation
            item.classList.remove('is-active');
            if (isActive) {
                void item.offsetWidth;
                item.classList.add('is-active');
            }
            tabs[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
            tabs[i].tabIndex = isActive ? 0 : -1;
        });
        images.forEach((image, i) => image.classList.toggle('is-active', i === current));
        if (focus) tabs[current].focus({preventScroll: true});
    };

    // line filled → next tab
    section.addEventListener('animationend', (e) => {
        if (e.animationName === 'auto-tabs-progress' && e.target.closest('.auto-tabs__item.is-active')) {
            activate(current + 1);
        }
    });

    tabs.forEach((tab, i) => tab.addEventListener('click', () => {
        if (i !== current) activate(i);
    }));

    section.querySelector('.auto-tabs__list').addEventListener('keydown', (e) => {
        const keys = {ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1};
        if (!(e.key in keys)) return;
        e.preventDefault();
        activate(current + keys[e.key], true);
    });

    // run only while visible
    let inView = false;
    const updatePause = () => section.classList.toggle('is-paused', !inView || document.hidden);
    new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        updatePause();
    }, {threshold: 0.25}).observe(section);
    document.addEventListener('visibilitychange', updatePause);
    updatePause();
};
